
import { defineComponent, ref, reactive, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonEntryRepository = RepositoryFactory.get('seasonEntry')
const TeamRepository = RepositoryFactory.get('team')
const CategoryRepository = RepositoryFactory.get('category')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog from '@/components/BaseDialog.vue'


import { Dialog } from '@/types/dialog'
import { Season } from '@/types/season'
import { TeamCategory } from '@/types/teamCategory'
import { EntryTeam } from '@/types/entryTeam'
import { Team } from '@/types/team'

export default defineComponent({
  name: 'SeasonEntry',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
    BaseDialog
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getEntries()
      getTeams()
      getTeamCategories()
    })

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const entries: {
      list: EntryTeam[]
      selected?: EntryTeam
    } = reactive({
      list: [],
      selected: undefined
    })

    const getEntries = async () => {
      try {
        const { season_id } = route.params

        const res = await SeasonEntryRepository.getEntries(season_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season
          entries.list = data.entries
        }
      } catch (error) {
        //
      }
    }

    const entryDialogDelete = reactive<Dialog>({
      title: '{チーム名}を{シーズン名}から削除しますか？',
      message: '削除すると、試合日程や結果が全て閲覧できなくなり、\n星取表などの表示が崩れる可能性があります。',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openEntryDialogDelete = (team: EntryTeam) => {
      entryDialogDelete.title = `${team.team_name} を ${season.data?.name} から削除しますか？`

      entries.selected = team
      entryDialogDelete.isOpen = true
    }

    const closeEntryDialogDelete = () => {
      entryDialogDelete.isOpen = false
      entries.selected = undefined
    }

    const deleteEntry = async () => {
      try {
        const { season_id } = route.params

        const dataObj = {
          entry_team_id: entries.selected?.id
        }

        const res = await SeasonEntryRepository.deleteEntry(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          entries.list = data.entries
          closeEntryDialogDelete()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'エントリーチームを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const teams: {
      list: Team[]
    } = reactive({
      list: []
    })

    const getTeams = async () => {
      try {
        const dataObj = {
          limit: entryAddCard.searchForm.limit,
          keyword: entryAddCard.searchForm.keyword
        }

        const res = await TeamRepository.getTeams(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          teams.list = data.teams
          entryAddCard.searchForm.others = data.total_number - 5
        }
      } catch (error) {
        //
      }
    }

    const updateEntryDivision = async (event: Event, team: EntryTeam) => {
      try {
        const { season_id } = route.params

        const dataObj = {
          entry_team_id: team.id,
          division: team.division
        }

        const res = await SeasonEntryRepository.updateEntry(season_id, dataObj)

        if (res.status === 200) {
          const target = event.target as HTMLInputElement
          target.blur()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'チームディビジョンを変更しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const entryAddCard = reactive({
      team_name: '',

      searchForm: {
        limit: 5,
        keyword: '',
        others: 0
      },

      isOpen: false,
      panel: 'entryAdd'
    })

    const openEntryAddCard = () => {
      entryAddCard.isOpen = true
      entryAddCard.panel = 'entryAdd'

      getTeams()
    }

    const closeEntryAddCard = () => {
      entryAddCard.searchForm.keyword = ''
      entryAddCard.isOpen = false
    }

    const changeEntryAddCardPanel = (panelType: string) => {
      entryAddCard.panel = panelType
    }

    const addEntry = async (id: number) => {
      try {
        const { season_id } = route.params

        const dataObj = {
          team_id: id
        }

        const res = await SeasonEntryRepository.postEntry(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          entries.list = data.entries
          closeEntryAddCard()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'エントリーチームを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const teamCategories: {
      list: TeamCategory[]
    } = reactive({
      list: []
    })

    const getTeamCategories = async () => {
      try {
        const res = await CategoryRepository.getTeamCategories()

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          teamCategories.list = data.categories
        }
      } catch (error) {
        //
      }
    }

    const teamForm = reactive({
      category_id: undefined,
      name: '',
      short_name: '',

      isCompleted: false
    })

    watch(
      () => [teamForm.category_id, teamForm.name, teamForm.short_name],
      () => {
        teamForm.isCompleted = (teamForm.category_id != undefined && teamForm.name !== '' && teamForm.short_name !== '') ? true : false
      },
      { immediate: true }
    )

    const addTeamAndEntry = async () => {
      try {
        const { season_id } = route.params

        const dataObj = {
          category_id: teamForm.category_id,
          name: teamForm.name,
          short_name: teamForm.short_name
        }

        const res = await SeasonEntryRepository.postTeamAndEntry(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          entries.list = data.entries
          closeEntryAddCard()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '新規チームを追加しました'
          })
        }
      } catch (error) {
        //
      }
    }

    useHead({
      title: computed(() => 'エントリーチーム - ' + season.data?.topic_name + season.data?.name),
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: '/event/' + route.params.topic_id + '/season/' + route.params.season_id,
          },
          setting: {
            path: '/event/' + route.params.topic_id
          }
        },
        {
          type: 'text',
          text: {
            name: 'エントリーチーム'
          }
        }
      ] as Breadcrumb[]
    })

    return {
      season,

      entries,
      getEntries,

      entryDialogDelete,
      openEntryDialogDelete,
      closeEntryDialogDelete,
      deleteEntry,

      teams,
      getTeams,

      updateEntryDivision,

      entryAddCard,
      openEntryAddCard,
      closeEntryAddCard,
      changeEntryAddCardPanel,

      addEntry,

      teamCategories,
      getTeamCategories,

      teamForm,
      addTeamAndEntry,

      breadcrumbs
    }
  }
})
